/**
 * Foundation extraction for the Agenda Builder. Given a parsed workbook and
 * the resolved Questions column for each tab, walks the column and returns
 * structured questions. The agenda renderer (template-driven) will plug
 * into the result of this function once the user shares the template.
 *
 * Each row's Questions cell may hold multiple questions joined by `\n\n` —
 * that's how `server/databook/builder.ts:1889` writes them. We split on
 * blank lines, trim, and drop empties.
 *
 * The line-item label is pulled from the leftmost non-empty text cell in
 * the same row. That's stable across the canonical IS / BS layouts; if a
 * foreign databook has a different label column we'll iterate later.
 */

import * as XLSX from "xlsx";
import type { AgendaDatabookPin } from "./storage";
import { RECOGNIZED_TABS, type RecognizedTab } from "./detect";

export type ExtractedQuestion = {
  /** 1-based source row in the underlying tab. */
  rowNum: number;
  /** Line-item label captured from the row's leftmost text cell. */
  lineItem: string;
  /** Single-question text. Multi-question cells produce one entry each. */
  question: string;
};

export type ExtractedTab = {
  tab: RecognizedTab;
  questions: ExtractedQuestion[];
};

export type ExtractionResult = {
  tabs: ExtractedTab[];
  totalQuestions: number;
};

export function extractQuestions(workbook: XLSX.WorkBook, pin: AgendaDatabookPin): ExtractionResult {
  const tabs: ExtractedTab[] = [];
  let total = 0;

  for (const tab of RECOGNIZED_TABS) {
    const col = pin.questionColByTab[tab];
    const headerRow = pin.headerRowByTab[tab] ?? 1;
    const actualSheet = pin.sheetNameByTab?.[tab] || tab;
    if (col == null) {
      tabs.push({ tab, questions: [] });
      continue;
    }
    const questions = extractTab(workbook, actualSheet, col, headerRow);
    tabs.push({ tab, questions });
    total += questions.length;
  }

  return { tabs, totalQuestions: total };
}

function extractTab(
  workbook: XLSX.WorkBook,
  actualSheetName: string,
  questionCol1Based: number,
  headerRow1Based: number,
): ExtractedQuestion[] {
  const sheet = workbook.Sheets[actualSheetName];
  if (!sheet || !sheet["!ref"]) return [];
  const range = XLSX.utils.decode_range(sheet["!ref"]!);
  const out: ExtractedQuestion[] = [];

  for (let r = headerRow1Based; r <= range.e.r; r++) {
    const cellRef = XLSX.utils.encode_cell({ r, c: questionCol1Based - 1 });
    const cell = sheet[cellRef];
    if (!cell || cell.v == null) continue;

    const text = String(cell.v).trim();
    if (!text) continue;

    const parts = text
      .split(/\n{2,}/) // builder.ts joins multi-question cells with \n\n
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length === 0) continue;

    const lineItem = leftmostLabel(sheet, range, r);
    for (const q of parts) {
      out.push({ rowNum: r + 1, lineItem, question: q });
    }
  }

  return out;
}

/** Leftmost non-empty cell in row `r` whose value looks like a line label
 * (string, not numeric / formula-only). Falls back to whatever non-empty
 * cell appears first. */
function leftmostLabel(sheet: XLSX.WorkSheet, range: XLSX.Range, r: number): string {
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = sheet[XLSX.utils.encode_cell({ r, c })];
    if (!cell || cell.v == null || cell.v === "") continue;
    if (typeof cell.v === "string" && cell.v.trim()) return cell.v.trim();
  }
  // Nothing string-typed; fall back to the first non-empty value (could be a number).
  for (let c = range.s.c; c <= range.e.c; c++) {
    const cell = sheet[XLSX.utils.encode_cell({ r, c })];
    if (!cell || cell.v == null) continue;
    return String(cell.v).trim();
  }
  return "";
}
